import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { ExtensionTypes } from 'generated/graphql-types';
import { EMPTY, Observable, of, tap } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { accountQueuesRoute } from 'src/app/app-routing.module';
import { getCurrentUserIsSystemUser } from 'src/app/authentication/state/authentication.selector';
import { CallDetailDialogComponent } from 'src/app/common-components/call-details-dialog/call-detail-dialog.component';
import { DialogSize } from 'src/app/common-components/confirmation-dialog/enumerations/dialog-size.enum';
import { LcsDialogService } from 'src/app/common-components/dialog/dialog.service';
import { getCallExtension } from 'src/app/common-components/helpers/call-logs-helper';
import { getHangUpCallsPrivileges } from 'src/app/privileges/privileges.selector';
import { ActiveCallHangupConfirmationDialogComponent } from '../../active-calls/active-call-hangup-confirmation-dialog/active-call-hangup-confirmation-dialog.component';
import { GetActiveCallSegmentsAndRecordingsForQueueCallDetails } from '../../active-calls/queries/generated/GetActiveCallSegmentsAndRecordingsForQueueCallDetails';
import { GetCallSegmentsAndRecordings } from '../../active-calls/queries/generated/GetCallSegmentsAndRecordings';
import { ActiveCallsRegisterApiService } from '../../active-calls/services/active-calls-register-api.service';
import { ActiveCallRegisterType } from '../../active-calls/services/active-calls-register-session-storage.service';
import { ExtensionOption } from '../../extension-register/models/extension-option.interface';
import { QueueHistoryFilterOptions } from '../models/queue-history-filter-option';
import { QueueHistoryOrderingOptions, QueueHistoryRegisterOptions } from '../models/queue-history-register-options';
import { LoggedInQueueMemberStatus } from '../models/queue-history-register-queue-members.model';
import {
   QueueAgentExtension,
   QueueExtension,
   QueueHistoryRegisterListItem,
   QueueHistoryRegisterResults,
} from '../models/queue-history-register-results.model';
import { GetAccountQueueMembers } from '../queries/generated/GetAccountQueueMembers';
import { GetQueueHistory, GetQueueHistory_queueHistoriesConnection_items } from '../queries/generated/GetQueueHistory';
import { QueueHistoryRegisterApiService } from '../services/queue-history-register-api.service';
import {
   QueueHistoryRegisterSessionStorageService,
   QueueHistoryRegisterStorageScope,
} from '../services/queue-history-register-session-storage.service';
import {
   clearQueueCall,
   clearQueueCallFailure,
   clearQueueCallSuccess,
   loadAccountQueueMembers,
   loadAccountQueueMembersFailure,
   loadAccountQueueMembersSuccess,
   openCallDetailsForActive,
   openCallDetailsForCallDetail,
   queueHistoryRegisterAdapterActions,
   sendToEmptyExitRoute,
   sendToEmptyExitRouteFailure,
   sendToEmptyExitRouteSuccess,
   setOrderingOptionsFromSessionStorage,
} from './queue-history-register.actions';
import {
   normalizeOrderingOptions,
   QueueHistoryRegisterState,
   selectEndCursor,
   selectQuickFilters,
} from './queue-history-register.reducer';
import * as QueueHistoryRegisterSelectors from './queue-history-register.selectors';
import { selectFilters, selectOrderingOptions } from './queue-history-register.selectors';

@Injectable()
export class QueueHistoryRegisterEffects {
   loadAccountQueueMembers$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(loadAccountQueueMembers),
         switchMap(({ accountId, reloadQueueHistoryRegister }) => {
            return this.queueHistoryRegisterApiService.getAccountQueueMembers(accountId).pipe(
               map((result) => {
                  return loadAccountQueueMembersSuccess({
                     ...this.convertGetAccountQueueMembersResult(result.data),
                     reloadQueueHistoryRegister,
                  });
               }),
               catchError((error) => {
                  return of(loadAccountQueueMembersFailure(error));
               })
            );
         })
      );
   });

   loadQueueHistoryList$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(queueHistoryRegisterAdapterActions.pageEnter, clearQueueCallSuccess, sendToEmptyExitRouteSuccess),
         withLatestFrom(
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([action, filters, quickFilters, orderingOptions]) => {
            const sessionOrderingOptions = this.sessionStorageService.getOrderingOptions(
               this.getOrderingStorageScope()
            );

            if (sessionOrderingOptions) {
               const defaultOrdering = orderingOptions ?? [];

               orderingOptions = normalizeOrderingOptions([
                  ...sessionOrderingOptions,
                  ...defaultOrdering.filter((o) => !sessionOrderingOptions.some((s) => s.field === o.field)),
               ]);

               this.store.dispatch(setOrderingOptionsFromSessionStorage({ orderingOptions }));
            }
            return this.loadNewData(filters, quickFilters, orderingOptions);
         })
      );
   });

   loadQueueHistoryPage$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(queueHistoryRegisterAdapterActions.loadPage),
         withLatestFrom(
            this.store.pipe(select(selectEndCursor)),
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),

         switchMap(([action, endCursor, quickFilters, filters, orderingOptions]) => {
            const sessionOrderingOptions = this.sessionStorageService.getOrderingOptions(
               this.getOrderingStorageScope()
            );

            if (sessionOrderingOptions) {
               const defaultOrdering = orderingOptions ?? [];

               orderingOptions = normalizeOrderingOptions([
                  ...sessionOrderingOptions,
                  ...defaultOrdering.filter((o) => !sessionOrderingOptions.some((s) => s.field === o.field)),
               ]);

               this.store.dispatch(setOrderingOptionsFromSessionStorage({ orderingOptions }));
            }
            return this.queueHistoryRegisterApiService
               .getQueueHistory(endCursor, filters, quickFilters, orderingOptions)
               .pipe(
                  map((result) => {
                     return queueHistoryRegisterAdapterActions.loadPageSuccess(
                        this.convertGetQueueHistoryResultToLoadSuccessProps(result.data)
                     );
                  }),
                  catchError((error) => {
                     return of(queueHistoryRegisterAdapterActions.loadPageFailure(error));
                  })
               );
         })
      );
   });

   quickFilterChanged$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(queueHistoryRegisterAdapterActions.quickFilterChanged),
         withLatestFrom(
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(QueueHistoryRegisterSelectors.selectFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([action, quickFilters, filters, orderingOptions]) => {
            return this.loadNewData(filters, quickFilters, orderingOptions);
         })
      );
   });

   orderingOptionsChanged$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(queueHistoryRegisterAdapterActions.orderingOptionsChanged),
         withLatestFrom(
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(QueueHistoryRegisterSelectors.selectFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([action, quickFilters, filters, orderingOptions]) => {
            this.sessionStorageService.setOrderingOptions(this.getOrderingStorageScope(), orderingOptions);
            return this.loadNewData(filters, quickFilters, orderingOptions);
         })
      );
   });

   openDialogForActiveCall$ = createEffect(
      () =>
         this.actions$.pipe(
            ofType(openCallDetailsForActive),
            withLatestFrom(
               this.store.pipe(select(getCurrentUserIsSystemUser)),
               this.store.pipe(select(getHangUpCallsPrivileges))
            ),
            switchMap(([action, isSystemUser, canHangupCalls]) =>
               this.activeCallsRegisterApiService
                  .getActiveCallDetailDialogDataForQueueHistory(action.queueHistory.callDetailId)
                  .pipe(
                     tap((callData) => {
                        this.openActiveCallDetailsDialog(callData, isSystemUser, canHangupCalls);
                     }),
                     catchError(() => this.handleOpenCallDetailsFailure())
                  )
            )
         ),
      { dispatch: false }
   );

   openDialogForCallDetailCall$ = createEffect(
      () =>
         this.actions$.pipe(
            ofType(openCallDetailsForCallDetail),
            withLatestFrom(
               this.store.pipe(select(getCurrentUserIsSystemUser)),
               this.store.pipe(select(getHangUpCallsPrivileges))
            ),
            switchMap(([action, isSystemUser, canHangupCalls]) =>
               this.activeCallsRegisterApiService.getCallDetailDialogData(action.queueHistory.callDetailId).pipe(
                  tap((callData) => {
                     this.openCallDetailDialog(callData, isSystemUser, canHangupCalls);
                  }),
                  catchError(async () => this.store.dispatch(openCallDetailsForActive(action)))
               )
            )
         ),
      { dispatch: false }
   );

   clearQueueCall$ = createEffect(() =>
      this.actions$.pipe(
         ofType(clearQueueCall),
         switchMap(({ queueHistory }) => {
            const { serverName, callerChannelId, callDetailId, queueHistoryId } = queueHistory;

            const message = `Are you sure you want to clear this call from the queue? If the call is still active, it will be hung up.`;
            const title = 'Clear Queue Call';

            const dialogRef = this.dialogService.open(ActiveCallHangupConfirmationDialogComponent, {
               data: { title, message, activeCall: queueHistory, isRoutingToEmptyExitRoute: false },
               panelClass: [DialogSize.Medium, 'dialog-container'],
               disableClose: true,
            });

            return dialogRef.afterClosed().pipe(
               switchMap((confirmed) => {
                  if (!confirmed) return EMPTY;

                  return this.activeCallsRegisterApiService
                     .hangupActiveCall(serverName?.split(',')[0] ?? null, callerChannelId, callDetailId)
                     .pipe(
                        catchError(() => of(null)),
                        switchMap(() =>
                           this.queueHistoryRegisterApiService.clearQueueHistory(queueHistoryId).pipe(
                              map(() => {
                                 this.snackbar.open('Call was successfully cleared.');
                                 return clearQueueCallSuccess();
                              }),
                              catchError((error) => of(clearQueueCallFailure(error)))
                           )
                        )
                     );
               })
            );
         })
      )
   );

   sendToEmptyExitRoute$ = createEffect(() =>
      this.actions$.pipe(
         ofType(sendToEmptyExitRoute),
         switchMap(({ queueHistory }) => {
            const { serverName, sipCallId } = queueHistory;
            const message = `Are you sure you want to send this call to the queue empty exit route?`;
            const title = 'Send Call To Empty Exit Route';

            const dialogRef = this.dialogService.open(ActiveCallHangupConfirmationDialogComponent, {
               data: { title, message, activeCall: queueHistory, isRoutingToEmptyExitRoute: true },
               panelClass: [DialogSize.Medium, 'dialog-container'],
               disableClose: true,
            });

            return dialogRef.afterClosed().pipe(
               switchMap((confirmed) => {
                  if (!confirmed) return EMPTY;

                  return this.activeCallsRegisterApiService.sendCallToEmptyExitRoute(serverName, sipCallId).pipe(
                     map(() => {
                        this.snackbar.open('Call was successfully routed.');
                        return sendToEmptyExitRouteSuccess();
                     }),
                     catchError((error) => of(sendToEmptyExitRouteFailure(error)))
                  );
               })
            );
         })
      )
   );

   constructor(
      private actions$: Actions,
      private store: Store<QueueHistoryRegisterState>,
      private queueHistoryRegisterApiService: QueueHistoryRegisterApiService,
      private activeCallsRegisterApiService: ActiveCallsRegisterApiService,
      private snackbar: MatSnackBar,
      private dialogService: LcsDialogService,
      private sessionStorageService: QueueHistoryRegisterSessionStorageService,
      private dialog: MatDialog,
      private router: Router
   ) {}

   private getOrderingStorageScope(): QueueHistoryRegisterStorageScope {
      return this.router.url.includes(accountQueuesRoute)
         ? QueueHistoryRegisterStorageScope.ActiveCalls
         : QueueHistoryRegisterStorageScope.QueueHistory;
   }

   private convertGetAccountQueueMembersResult(getAccountQueueMembers: GetAccountQueueMembers | null | undefined): {
      queueExtensions: ExtensionOption[];
      allAccountQueueMembers: LoggedInQueueMemberStatus[];
   } {
      const extensionItems = getAccountQueueMembers?.extensionsConnection?.items ?? [];
      const queueExtensions: ExtensionOption[] = extensionItems.map((item) => {
         return {
            extensionId: item.extensionId,
            name: item.name,
            shortName: item.shortName,
            number: item.number ? Number(item.number) : null,
            type: ExtensionTypes.CallQueue,
         };
      });

      const allAccountQueueMembers = extensionItems.reduce((members, queueExtension) => {
         const queueMembers = queueExtension.extensionQueue?.queueMembersConnection?.items ?? [];
         queueMembers.forEach((queueMember) => {
            members.push({
               queueMemberId: queueMember.queueMemberId,
               queueExtensionId: queueExtension.extensionId,
               queueName: this.getQueueDisplayName(queueExtension.shortName, queueExtension.name),
               queueNumber: queueExtension.number ? Number(queueExtension.number) : null,
               isLoggedIn: !!queueMember.isLoggedIn,
               agentStatus: queueMember.agentStatus ?? null,
               endWrapUpTime: queueMember.endWrapUpTime ? new Date(queueMember.endWrapUpTime) : null,
               agentExtensionId: queueMember.agentExtension?.extensionId ?? null,
               agentName: queueMember.agentExtension?.name ?? null,
               agentNumber: queueMember.agentExtension?.number ? String(queueMember.agentExtension.number) : null,
            });
         });

         return members;
      }, [] as LoggedInQueueMemberStatus[]);

      return {
         queueExtensions,
         allAccountQueueMembers,
      };
   }

   private getQueueDisplayName(shortName: string | null | undefined, name: string | null | undefined): string | null {
      return shortName?.trim() || name?.trim() || null;
   }

   private convertGetQueueHistoryResultToLoadSuccessProps(
      getQueueHistory: GetQueueHistory
   ): QueueHistoryRegisterResults {
      return {
         endCursor: getQueueHistory.queueHistoriesConnection.pageInfo.endCursor,
         hasNextPage: getQueueHistory.queueHistoriesConnection.pageInfo.hasNextPage,
         items: getQueueHistory.queueHistoriesConnection.items.map(this.convertQueueHistoryRegisterListItem),
         totalCount: getQueueHistory.queueHistoriesConnection.totalCount,
      };
   }

   private convertQueueHistoryRegisterListItem(
      getQueueHistoryItem: GetQueueHistory_queueHistoriesConnection_items
   ): QueueHistoryRegisterListItem {
      return {
         ...getQueueHistoryItem,
         queueExtensionId: getQueueHistoryItem.queueExtensionId,
         queueExtension: (getQueueHistoryItem.queueExtension as QueueExtension) ?? undefined,
         callDetailId:
            getQueueHistoryItem?.activeCall?.callDetailId ?? getQueueHistoryItem?.callDetail?.callDetailId ?? null,
         callerChannelId:
            getQueueHistoryItem?.activeCall?.callerChannelId ??
            getQueueHistoryItem?.callDetail?.callerChannelId ??
            null,
         serverName: getQueueHistoryItem?.activeCall?.serverName ?? getQueueHistoryItem?.callDetail?.serverName ?? null,
         sipCallId: getQueueHistoryItem?.activeCall?.sipCallId ?? getQueueHistoryItem?.callDetail?.sipCallId ?? null,
         hasPreventEmptyQueue:
            getQueueHistoryItem?.activeCall?.destinationExtension?.extensionQueue?.hasPreventEmptyQueue ??
            getQueueHistoryItem?.callDetail?.destinationExtension?.extensionQueue?.hasPreventEmptyQueue ??
            false,
         callerIdNumber:
            getQueueHistoryItem.callerIdNumber?.startsWith('1') && getQueueHistoryItem.callerIdNumber.length > 4
               ? getQueueHistoryItem.callerIdNumber.substring(1)
               : getQueueHistoryItem.callerIdNumber,
         agentExtension: (getQueueHistoryItem.agentExtension as QueueAgentExtension) ?? undefined,
      };
   }

   private loadNewData(
      filters: QueueHistoryFilterOptions[],
      quickFilters: QueueHistoryRegisterOptions | null = null,
      orderingOptions: QueueHistoryOrderingOptions[] | null = null
   ) {
      return this.queueHistoryRegisterApiService.getQueueHistory(null, filters, quickFilters, orderingOptions).pipe(
         map((result) => {
            return queueHistoryRegisterAdapterActions.loadSuccess(
               this.convertGetQueueHistoryResultToLoadSuccessProps(result.data)
            );
         }),
         catchError((error) => {
            return of(queueHistoryRegisterAdapterActions.loadFailure(error));
         })
      );
   }

   private openActiveCallDetailsDialog(
      callData: { data?: GetActiveCallSegmentsAndRecordingsForQueueCallDetails | null } | null | undefined,
      isSystemUser: boolean,
      canHangupCalls
   ): void {
      const activeCall = callData?.data?.activeCall;

      if (!activeCall) {
         this.snackbar.open('Call details are unavailable.');
         return;
      }

      this.dialog.open(CallDetailDialogComponent, {
         data: {
            callLogs: this.convertCallForDialog(activeCall),
            callData: activeCall,
            relatedCallDetails: (callData as any).relatedCallDetails,
            isSystemUser,
            canHangupCalls: canHangupCalls,
            registerType: ActiveCallRegisterType.AccountActive,
         },
         width: '800px',
         autoFocus: false,
      });
   }

   private openCallDetailDialog(
      callData: { data?: GetCallSegmentsAndRecordings | null } | null | undefined,
      isSystemUser: boolean,
      canHangupCalls
   ): void {
      const callDetail = callData?.data?.callDetail;

      if (!callDetail) {
         this.snackbar.open('Call details are unavailable.');
         return;
      }

      this.dialog.open(CallDetailDialogComponent, {
         data: {
            callLogs: this.convertCallForDialog(callDetail),
            callData: callDetail,
            relatedCallDetails: (callData as any).relatedCallDetails,
            isSystemUser,
            canHangupCalls: canHangupCalls,
            registerType: ActiveCallRegisterType.AccountActive,
         },
         width: '800px',
         autoFocus: false,
      });
   }

   private handleOpenCallDetailsFailure(): Observable<never> {
      this.snackbar.open('Unable to load call details.');
      return EMPTY;
   }

   private convertCallForDialog(item: any): any {
      return {
         accountName: item.account?.name ?? null,

         callDestination: getCallExtension(
            { ...item.destinationExtension },
            item.callDirectionTypeId,
            item.destinationNumber,
            true
         ),

         callDetailId: item.callDetailId,
         callDirection: item.callDirectionTypeId,

         callSource: getCallExtension({ ...item.sourceExtension }, item.callDirectionTypeId, item.sourceNumber),

         destinationExtension: item.destinationExtension,
         destinationNumber: item.destinationNumber,
         originalDestinationNumber: item.originalDestinationNumber,

         phoneNumber: null,
         callRate: 0,
         startTime: item.startTime,
         endTime: item.endTime ?? null,

         callerChannelId: item.callerChannelId ?? null,
         serverName: item.serverName,
         sipCallId: item.sipCallId,
         providerId: item.providerId,

         callRecordingFile: item.callRecording?.file ?? null,
      };
   }
}
